'use client';

import React from 'react';
import Summary from '@/components/job-title/summary';
import SubscribeJob from '@/components/subscribe-job';
import SalaryAndBenefits from '@/components/job-title/tabs/salary-and-benefits-tab';
import JobFuture from '@/components/job-title/tabs/job-future-tab';
import CityComparison from '@/components/job-title/tabs/city-comparison-tab';
import HotJobOpportunity from '@/components/job-title/HotJobOpportunity';
import SearchArea from '@/components/job-title/search-area';
import TabNav from '@/components/job-title/tabs/TabNav';
import SimilarJobTitle from '@/components/job-title/similar-job-title';
import MoreJobTitle from '@/components/job-title/MoreJobTitle';
import RenderTabs from '@/components/RenderTabs';
import MiddleBanner from '@/components/job-title/marketing/MiddleBanner';
import RecommendPosts, { PostType } from '@/components/home-page/RecommendPost';
import { JobOpportunityType } from '@/components/job-opportunity-panel';
import { AuthContext, JobTitleContext } from '@/context/create-context';
import {
  JobTitleDataValue,
  JobTitleSummaryValue,
  SimilarJobTitleType,
  MoreJobTitleType,
} from '@/context/job-title';
import {
  STORAGE_KEY_VALUE,
  TAB_CITY_COMPARISON,
  TAB_JOB_FUTURE,
  TAB_MAP,
  TAB_SALARY_AND_WELFARE,
} from '@/constants/job-title';
import { useBodyClassName, useClintMediaQuery } from '@/utils/hooks';
import { getJobTitlePagePosts } from '@/utils/articles';
import {
  getHotJobOpportunities,
  getJobTitleVote,
  getVotePermission,
} from '@/api/tanzhi';

type JobTitleProps = {
  jobTitle: JobTitleDataValue;
  jobTitleSummary: JobTitleSummaryValue;
  similarJobTitles: SimilarJobTitleType[];
  moreJobTitles: MoreJobTitleType[];
  jobTitleId: string;
  jobTitleCity?: string;
};

export const TabsContent = [
  {
    key: TAB_SALARY_AND_WELFARE,
    id: TAB_SALARY_AND_WELFARE + '-tab',
    name: TAB_MAP[TAB_SALARY_AND_WELFARE],
    node: <SalaryAndBenefits />,
  },
  {
    key: TAB_JOB_FUTURE,
    id: TAB_JOB_FUTURE + '-tab',
    name: TAB_MAP[TAB_JOB_FUTURE],
    node: <JobFuture />,
  },
  {
    key: TAB_CITY_COMPARISON,
    id: TAB_CITY_COMPARISON + '-tab',
    name: TAB_MAP[TAB_CITY_COMPARISON],
    node: <CityComparison />,
  },
];

const JobTitle = (props: JobTitleProps) => {
  const {
    jobTitle,
    jobTitleSummary,
    similarJobTitles,
    moreJobTitles,
    jobTitleId,
    jobTitleCity,
  } = props;
  const { user } = React.useContext(AuthContext);
  const isLogin = !!user && user?.is_authenticated;
  const isMobile = useClintMediaQuery();

  const [rate, setRate] = React.useState<Record<string, number> | null>(null);
  const [showVoteCard, setShowVoteCard] = React.useState(false);
  const [hotJobOpportunities, setHotJobOpportunities] = React.useState<
    JobOpportunityType[]
  >([]);
  const [posts, setPosts] = React.useState<PostType[]>([]);
  const generalEl = React.useRef<any>(null);

  useBodyClassName(
    '[background:linear-gradient(180deg,rgba(255,255,255,0)_0%,#fff_98.49%),linear-gradient(90deg,rgba(244,215,253,0.2)_0%,rgba(54,192,246,0.2)_100%),linear-gradient(90deg,rgba(216,253,250,0.5)_0%,rgba(253,216,253,0.5)_100%),#fff]',
  );

  React.useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY_VALUE,
      JSON.stringify({ id: jobTitleId, name: jobTitle.display_name }),
    );
    getHotJobOpportunities({
      jobTitle: jobTitleId,
      city: jobTitleCity,
      size: 16,
    }).then((res) => {
      if (res.status == 200) {
        setHotJobOpportunities(res.data);
      }
    });
    getJobTitlePagePosts(jobTitle).then((data) => {
      if (data && data.length > 0) {
        setPosts(data);
      }
    });
  }, []);

  React.useEffect(() => {
    if (isLogin) {
      const { id } = jobTitle;
      getVotePermission(id)
        .then((res) => {
          if (res.data.code === 200) {
            setShowVoteCard(true);
            getJobTitleVote(id).then((res) => {
              const rating = res.data.rating;
              rating && setRate(rating);
            });
          }
        })
        .catch(() => setShowVoteCard(false));
    } else {
      setShowVoteCard(false);
    }
  }, [isLogin]);

  return (
    <JobTitleContext.Provider
      value={{
        jobTitle,
        jobTitleSummary,
        displayName: jobTitle.display_name,
      }}
    >
      <div ref={generalEl} className="mb-9 tablet:mb-14">
        <div className="pt-10"></div>
        <SearchArea />
        <Summary />
        <MiddleBanner />
        <HotJobOpportunity
          data={hotJobOpportunities}
          noSwiper
          displayName={jobTitle.display_name}
        />
        <SubscribeJob
          dataEleId="subscribe_job-title_middle"
          dataObserverId="subscribe_job-title_middle"
        />
      </div>
      <div>
        <TabNav generalEl={generalEl} tabsClassName="" isLogin={isLogin} />
        <RenderTabs
          content={TabsContent}
          recommendPost={posts?.[0]}
          showVoteCard={showVoteCard}
          rate={rate}
          showLocation={true}
          locationSalary={jobTitle.location_salary}
        />
        {similarJobTitles?.length > 0 && (
          <SimilarJobTitle data={similarJobTitles} />
        )}
        {moreJobTitles?.length > 0 && <MoreJobTitle data={moreJobTitles} />}
        {isMobile && (
          <div className="block tablet:hidden">
            <RenderTabs
              content={TabsContent}
              recommendPost={posts?.[0]}
              showVoteCard={showVoteCard}
              rate={rate}
              showLeft={false}
              showLocation={true}
              locationSalary={jobTitle.location_salary}
            />
          </div>
        )}
        <RecommendPosts posts={posts} />
      </div>
    </JobTitleContext.Provider>
  );
};

export default JobTitle;
